import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Collapse,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  DataObject as EntityIcon,
  ViewColumn as FieldIcon,
  Build as ToolIcon,
  Api as ApiIcon,
  Close as CloseIcon,
  UnfoldMore as FullscreenIcon,
  UnfoldLess as ExitFullscreenIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  PlayCircleOutline as ActionIcon,
  Code as ScriptIcon,
  Psychology as PromptIcon,
  Cloud as ConnectedSystemIcon,
  NetworkCheck as TestConnectionIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import FlexService from '../../services/FlexService';
import PromptService from '../../services/PromptService';
import ReadOnlyJsonField from './ReadOnlyJsonField';
import { useAuth } from '../../contexts/AuthContext';

// Helper to convert camelCase to Title Case (e.g., "connectedSystem" → "Connected System")
const formatTypeName = (type) => {
  if (!type) return '';
  return type
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

/**
 * FlexRegistryPanel - Admin panel for managing Connected Systems, APIs, Tools, Scripts, and Prompts
 * Note: Entities (Domains), Fields (Attributes), and Actions are now in CatalogWorkspace
 */
function FlexRegistryPanel({ isCollapsed = true, onToggleCollapse, standalone = false }) {
  const { hasRole } = useAuth();
  // Matches backend UserPrincipal.isPlatformAdmin() exactly (hasRole("FOYSONIS_DEV")) --
  // a base/global API (systemCode null) is shared product definition, not owned
  // by any one tenant, so only a platform admin may delete it.
  const isPlatformAdmin = hasRole('FOYSONIS_DEV');

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Data states
  const [entities, setEntities] = useState([]);
  const [fields, setFields] = useState([]);
  const [actions, setActions] = useState([]);
  const [apis, setApis] = useState([]);
  const [tools, setTools] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [connectedSystems, setConnectedSystems] = useState([]);
  const [summary, setSummary] = useState(null);

  // Connected system health status
  const [systemHealthStatus, setSystemHealthStatus] = useState({});

  // Dialog states
  const [editDialog, setEditDialog] = useState({ open: false, type: null, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });

  // Endpoint management dialog state
  const [endpointDialog, setEndpointDialog] = useState({ open: false, system: null, endpoints: [], loading: false });
  const [editingEndpoint, setEditingEndpoint] = useState(null);

  // Filter state
  const [selectedEntity, setSelectedEntity] = useState('');

  // API health status: { [apiId]: 'pending' | 'ok' | 'error' }
  const [apiHealthStatus, setApiHealthStatus] = useState({});

  // Toggle fullscreen
  const toggleFullscreen = (e) => {
    e.stopPropagation();
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen && isCollapsed) {
      onToggleCollapse();
    }
  };

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Load summary on mount
  useEffect(() => {
    loadSummary();
  }, []);

  // Load data when tab changes
  useEffect(() => {
    if (!isCollapsed) {
      loadTabData(activeTab);
    }
  }, [activeTab, isCollapsed]);

  const loadSummary = async () => {
    try {
      const data = await FlexService.getRegistrySummary();
      setSummary(data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  };

  const loadTabData = async (tabIndex) => {
    setLoading(true);
    setError(null);
    try {
      switch (tabIndex) {
        case 0: // Connected Systems
          const systemsData = await FlexService.listConnectedSystems();
          setConnectedSystems(systemsData);
          // Auto-ping all systems to check health
          pingAllSystems(systemsData);
          break;
        case 1: // APIs
          const apisData = await FlexService.listApis();
          setApis(apisData);
          // Auto-ping all APIs to check health
          pingAllApis(apisData);
          break;
        case 2: // Tools
          const toolsData = await FlexService.listTools();
          setTools(toolsData);
          break;
        case 3: // Scripts
          const scriptsData = await FlexService.listScripts();
          setScripts(scriptsData);
          break;
        case 4: // Prompts
          const promptsData = await PromptService.listPrompts({});
          setPrompts(promptsData.prompts || []);
          break;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Ping all APIs to check their health status
  const pingAllApis = useCallback((apiList) => {
    // Initialize all as pending
    const initialStatus = {};
    apiList.forEach(api => {
      initialStatus[api.id] = 'pending';
    });
    setApiHealthStatus(initialStatus);

    // Ping each API asynchronously
    apiList.forEach(api => {
      pingApi(api);
    });
  }, []);

  // Ping a single API
  const pingApi = async (api) => {
    try {
      const result = await FlexService.pingApi(api.id);
      setApiHealthStatus(prev => ({
        ...prev,
        [api.id]: result.success ? 'ok' : 'error'
      }));
    } catch (err) {
      setApiHealthStatus(prev => ({
        ...prev,
        [api.id]: 'error'
      }));
    }
  };

  // Ping all connected systems to check their health status
  const pingAllSystems = useCallback((systemList) => {
    // Initialize all as pending
    const initialStatus = {};
    systemList.forEach(sys => {
      initialStatus[sys.id] = 'pending';
    });
    setSystemHealthStatus(initialStatus);

    // Ping each system asynchronously
    systemList.forEach(sys => {
      pingSystem(sys);
    });
  }, []);

  // Ping a single connected system
  const pingSystem = async (system) => {
    try {
      const result = await FlexService.testConnectedSystem(system.id);
      setSystemHealthStatus(prev => ({
        ...prev,
        [system.id]: result.success ? 'ok' : 'error'
      }));
    } catch (err) {
      setSystemHealthStatus(prev => ({
        ...prev,
        [system.id]: 'error'
      }));
    }
  };

  // Render system health status icon
  const renderSystemHealthIcon = (systemId) => {
    const status = systemHealthStatus[systemId];
    switch (status) {
      case 'ok':
        return (
          <Tooltip title="System is reachable">
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
          </Tooltip>
        );
      case 'error':
        return (
          <Tooltip title="System is not reachable">
            <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
          </Tooltip>
        );
      case 'pending':
      default:
        return (
          <Tooltip title="Checking...">
            <CircularProgress size={14} />
          </Tooltip>
        );
    }
  };

  // Render health status icon
  const renderHealthIcon = (apiId) => {
    const status = apiHealthStatus[apiId];
    switch (status) {
      case 'ok':
        return (
          <Tooltip title="API is reachable">
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
          </Tooltip>
        );
      case 'error':
        return (
          <Tooltip title="API is not reachable">
            <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
          </Tooltip>
        );
      case 'pending':
      default:
        return (
          <Tooltip title="Checking...">
            <CircularProgress size={14} />
          </Tooltip>
        );
    }
  };

  const handleRefresh = () => {
    loadTabData(activeTab);
    loadSummary();
  };

  // Endpoint management functions
  const openEndpointDialog = async (system) => {
    setEndpointDialog({ open: true, system, endpoints: [], loading: true });
    try {
      const endpoints = await FlexService.listConnectedSystemEndpoints(system.id);
      setEndpointDialog(prev => ({ ...prev, endpoints: endpoints || [], loading: false }));
    } catch (err) {
      setError('Failed to load endpoints: ' + err.message);
      setEndpointDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleAddEndpoint = () => {
    setEditingEndpoint({
      endpointType: '',
      url: '',
      description: '',
      isPublic: false,
      allowedMethods: '',
      authType: 'API_KEY',
      isActive: true,
    });
  };

  const handleEditEndpoint = (endpoint) => {
    setEditingEndpoint({ ...endpoint });
  };

  const handleSaveEndpoint = async () => {
    if (!editingEndpoint || !endpointDialog.system) return;
    try {
      if (editingEndpoint.id) {
        await FlexService.updateConnectedSystemEndpoint(endpointDialog.system.id, editingEndpoint.id, editingEndpoint);
      } else {
        await FlexService.createConnectedSystemEndpoint(endpointDialog.system.id, editingEndpoint);
      }
      // Reload endpoints
      const endpoints = await FlexService.listConnectedSystemEndpoints(endpointDialog.system.id);
      setEndpointDialog(prev => ({ ...prev, endpoints: endpoints || [] }));
      setEditingEndpoint(null);
    } catch (err) {
      setError('Failed to save endpoint: ' + err.message);
    }
  };

  const handleDeleteEndpoint = async (endpoint) => {
    if (!endpointDialog.system) return;
    try {
      await FlexService.deleteConnectedSystemEndpoint(endpointDialog.system.id, endpoint.id);
      const endpoints = await FlexService.listConnectedSystemEndpoints(endpointDialog.system.id);
      setEndpointDialog(prev => ({ ...prev, endpoints: endpoints || [] }));
    } catch (err) {
      setError('Failed to delete endpoint: ' + err.message);
    }
  };

  const handleEdit = (type, item) => {
    setEditDialog({ open: true, type, item: item || {} });
  };

  const handleSave = async () => {
    const { type, item } = editDialog;
    try {
      switch (type) {
        case 'entity':
          await FlexService.saveEntity(item);
          break;
        case 'field':
          await FlexService.saveField(item);
          break;
        case 'action':
          await FlexService.saveAction(item);
          break;
        case 'api':
          await FlexService.saveApi(item);
          break;
        case 'tool':
          await FlexService.saveTool(item);
          break;
        case 'prompt':
          if (item.promptId) {
            await PromptService.updatePrompt(item.promptId, item);
          } else {
            await PromptService.createPrompt(item);
          }
          break;
        case 'connectedSystem':
          if (item.id) {
            await FlexService.updateConnectedSystem(item.id, item);
          } else {
            await FlexService.createConnectedSystem(item);
          }
          break;
      }
      setEditDialog({ open: false, type: null, item: null });
      handleRefresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    const { type, item } = deleteDialog;
    try {
      switch (type) {
        case 'entity':
          await FlexService.deleteEntity(item.id);
          break;
        case 'field':
          await FlexService.deleteField(item.id);
          break;
        case 'action':
          await FlexService.deleteAction(item.id);
          break;
        case 'api':
          await FlexService.deleteApi(item.id);
          break;
        case 'tool':
          await FlexService.deleteTool(item.toolId);
          break;
        case 'script':
          await FlexService.deleteScript(item.id);
          break;
        case 'prompt':
          await PromptService.deletePrompt(item.promptId);
          break;
        case 'connectedSystem':
          await FlexService.deleteConnectedSystem(item.id);
          break;
      }
      setDeleteDialog({ open: false, type: null, item: null });
      handleRefresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const updateEditItem = (field, value) => {
    setEditDialog(prev => ({
      ...prev,
      item: { ...prev.item, [field]: value }
    }));
  };

  // Filter data by search term
  const filterData = (data, keys) => {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(item =>
      keys.some(key => item[key]?.toString().toLowerCase().includes(term))
    );
  };

  const filteredEntities = filterData(entities, ['entityName', 'displayName', 'description']);
  const filteredFields = filterData(fields, ['fieldName', 'displayLabel', 'entityName']);
  const filteredActions = filterData(actions, ['functionName', 'entityName', 'description']);
  const filteredApis = filterData(apis, ['apiKey', 'apiName', 'description', 'endpointPath']);
  const filteredTools = filterData(tools, ['toolKey', 'toolName', 'description']);
  const filteredScripts = filterData(scripts, ['name', 'description']);
  const filteredPrompts = filterData(prompts, ['name', 'description', 'category', 'domain']);

  const filteredConnectedSystems = filterData(connectedSystems, ['systemCode', 'name', 'systemType', 'baseUrl']);

  // Note: Data Masters, Attributes, and Actions are now in Catalog workspace
  const tabLabels = [
    { label: 'Connected Systems', icon: <ConnectedSystemIcon />, count: connectedSystems.length, type: 'connectedSystem' },
    { label: 'APIs', icon: <ApiIcon />, count: summary?.apis?.total, type: 'api' },
    { label: 'Agents / Tools', icon: <ToolIcon />, count: summary?.tools?.total, type: 'tool' },
  ];

  // In standalone mode, always expanded and no header
  const effectiveCollapsed = standalone ? false : isCollapsed;
  const effectiveFullscreen = standalone || isFullscreen;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderTop: standalone ? 0 : 1,
        borderColor: 'divider',
        height: standalone ? '100%' : 'auto',
        display: effectiveFullscreen ? 'flex' : 'block',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(isFullscreen && !standalone && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          borderTop: 0,
        }),
      }}
    >
      {/* Header - hidden in standalone mode */}
      {!standalone && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.5,
            borderBottom: effectiveCollapsed ? 0 : 1,
            borderColor: 'divider',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={onToggleCollapse}
        >
          <IconButton size="small" sx={{ p: 0.5 }}>
            {effectiveCollapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
          </IconButton>

          <ToolIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
            Registry Manager
          </Typography>

          {summary && (
            <Chip
              label={`${connectedSystems.length} Systems | ${summary.apis?.total || 0} APIs | ${summary.tools?.total || 0} Tools`}
              size="small"
              sx={{ height: 20, fontSize: 10 }}
            />
          )}

          <Tooltip title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand to fullscreen'}>
            <IconButton
              size="small"
              onClick={toggleFullscreen}
              sx={{ ml: 1 }}
            >
              {isFullscreen ? <ExitFullscreenIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Content - Rendered differently for standalone/fullscreen vs collapsible modes */}
      {effectiveFullscreen ? (
        // Standalone/Fullscreen: Direct flex layout without Collapse
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 2 }}>
          {/* Toolbar */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexShrink: 0 }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={handleRefresh}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleEdit(tabLabels[activeTab].type, null)}
            >
              Add New
            </Button>
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, minHeight: 36, flexShrink: 0 }}
          >
            {tabLabels.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {tab.label}
                    {tab.count !== undefined && (
                      <Chip label={tab.count} size="small" sx={{ height: 18, fontSize: 10 }} />
                    )}
                  </Box>
                }
                sx={{ minHeight: 36, py: 0.5, textTransform: 'none' }}
              />
            ))}
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Data Table - Takes remaining space with scroll */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: '100%' }}>
              <Table size="small" stickyHeader>
                {/* Connected Systems - Tab 0 */}
                {activeTab === 0 && (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" sx={{ width: 50 }}>Status</TableCell>
                        <TableCell>System Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Base URL</TableCell>
                        <TableCell>API Key</TableCell>
                        <TableCell>Endpoints</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredConnectedSystems.map((system) => (
                        <TableRow key={system.id} hover>
                          <TableCell align="center">{renderSystemHealthIcon(system.id)}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{system.systemCode}</TableCell>
                          <TableCell>{system.name}</TableCell>
                          <TableCell><Chip label={system.systemType || 'WMS'} size="small" color="primary" /></TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {system.baseUrl || '-'}
                          </TableCell>
                          <TableCell>
                            {system.hasApiKey ? (
                              <Chip label={system.apiKey} size="small" sx={{ fontFamily: 'monospace', fontSize: 10 }} />
                            ) : (
                              <Typography variant="caption" color="text.secondary">Not set</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={system.endpointCount || 0} size="small" />
                          </TableCell>
                          <TableCell>{system.isActive ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Test Connection">
                              <IconButton size="small" onClick={() => pingSystem(system)}>
                                <TestConnectionIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Manage Endpoints">
                              <IconButton size="small" onClick={() => openEndpointDialog(system)}>
                                <SettingsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={() => handleEdit('connectedSystem', system)}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, type: 'connectedSystem', item: system })}><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}

                {/* APIs - Tab 1 */}
                {activeTab === 1 && (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" sx={{ width: 50 }}>Status</TableCell>
                        <TableCell>API Key</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Endpoint</TableCell>
                        <TableCell>Entity</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell align="right">Edit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredApis.map((api) => (
                        <TableRow key={api.id} hover>
                          <TableCell align="center">{renderHealthIcon(api.id)}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{api.apiKey}</TableCell>
                          <TableCell>{api.apiName}</TableCell>
                          <TableCell><Chip label={api.httpMethod} size="small" color={api.httpMethod === 'GET' ? 'success' : api.httpMethod === 'POST' ? 'primary' : 'warning'} /></TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{api.endpointPath}</TableCell>
                          <TableCell>{api.entityName}</TableCell>
                          <TableCell>{api.isActive ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEdit('api', api)}><EditIcon fontSize="small" /></IconButton>
                            {(api.systemCode != null || isPlatformAdmin) ? (
                              <IconButton size="small" onClick={() => setDeleteDialog({ open: true, type: 'api', item: api })}><DeleteIcon fontSize="small" /></IconButton>
                            ) : (
                              <Tooltip title="Base (global) API definitions can only be deleted by a platform admin">
                                <span>
                                  <IconButton size="small" disabled><DeleteIcon fontSize="small" /></IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}

                {/* Tools - Tab 2 */}
                {activeTab === 2 && (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tool Key</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Domain</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell align="right">Edit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTools.map((tool) => (
                        <TableRow key={tool.toolId} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{tool.toolKey}</TableCell>
                          <TableCell>{tool.toolName}</TableCell>
                          <TableCell><Chip label={tool.domain} size="small" /></TableCell>
                          <TableCell><Chip label={tool.category} size="small" color="secondary" /></TableCell>
                          <TableCell>{tool.active ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEdit('tool', tool)}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, type: 'tool', item: tool })}><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}

                {/* Scripts - Tab 3 */}
                {activeTab === 3 && (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableCell>Script Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Locked</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredScripts.map((script) => (
                        <TableRow key={script.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{script.name}</TableCell>
                          <TableCell>{script.description || '-'}</TableCell>
                          <TableCell>{script.createdBy || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={script.isLocked ? 'Locked' : 'Unlocked'}
                              size="small"
                              color={script.isLocked ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{script.updatedAt ? new Date(script.updatedAt).toLocaleDateString() : '-'}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => window.open(`/flex-ide?scriptId=${script.id}`, '_blank')}
                              title="Open in FLEX IDE"
                            >
                              <ScriptIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, type: 'script', item: script })}><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}

                {/* Prompts - Tab 4 */}
                {activeTab === 4 && (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableCell>Prompt Name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Domain</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Favorite</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPrompts.map((prompt) => (
                        <TableRow key={prompt.promptId} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{prompt.name}</TableCell>
                          <TableCell><Chip label={prompt.category || 'general'} size="small" /></TableCell>
                          <TableCell><Chip label={prompt.domain || 'all'} size="small" color="info" /></TableCell>
                          <TableCell>
                            <Chip
                              label={prompt.status || 'draft'}
                              size="small"
                              color={prompt.status === 'published' ? 'success' : prompt.status === 'archived' ? 'error' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{prompt.favorite ? '★' : '-'}</TableCell>
                          <TableCell>{prompt.updatedAt ? new Date(prompt.updatedAt).toLocaleDateString() : '-'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEdit('prompt', prompt)}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, type: 'prompt', item: prompt })}><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
              </Table>
            </TableContainer>
          )}
          </Box>
        </Box>
      ) : (
        // Collapsed mode - use Collapse with fixed height
        <Collapse in={!effectiveCollapsed}>
          <Box sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
            <Typography color="text.secondary">Expand to view registry</Typography>
          </Box>
        </Collapse>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, type: null, item: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          {editDialog.item?.id || editDialog.item?.toolId ? 'Edit' : 'Create'} {formatTypeName(editDialog.type)}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editDialog.type === 'entity' && (
              <>
                <TextField label="Entity Name" value={editDialog.item?.entityName || ''} onChange={(e) => updateEditItem('entityName', e.target.value)} fullWidth />
                <TextField label="Display Name" value={editDialog.item?.displayName || ''} onChange={(e) => updateEditItem('displayName', e.target.value)} fullWidth />
                <TextField label="Display Name Plural" value={editDialog.item?.displayNamePlural || ''} onChange={(e) => updateEditItem('displayNamePlural', e.target.value)} fullWidth />
                <TextField label="Description" value={editDialog.item?.description || ''} onChange={(e) => updateEditItem('description', e.target.value)} fullWidth multiline rows={2} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Domain</InputLabel>
                    <Select value={editDialog.item?.domain || 'WMS'} label="Domain" onChange={(e) => updateEditItem('domain', e.target.value)}>
                      <MenuItem value="WMS">WMS</MenuItem>
                      <MenuItem value="FAST">FAST</MenuItem>
                      <MenuItem value="GENERAL">GENERAL</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Data Source</InputLabel>
                    <Select value={editDialog.item?.dataSource || 'INTERNAL'} label="Data Source" onChange={(e) => updateEditItem('dataSource', e.target.value)}>
                      <MenuItem value="INTERNAL">INTERNAL</MenuItem>
                      <MenuItem value="WMS">WMS</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <FormControlLabel control={<Switch checked={editDialog.item?.isActive ?? true} onChange={(e) => updateEditItem('isActive', e.target.checked)} />} label="Active" />
              </>
            )}

            {editDialog.type === 'field' && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Entity Name" value={editDialog.item?.entityName || ''} onChange={(e) => updateEditItem('entityName', e.target.value)} fullWidth />
                  <TextField label="Field Name" value={editDialog.item?.fieldName || ''} onChange={(e) => updateEditItem('fieldName', e.target.value)} fullWidth />
                </Box>
                <TextField label="Display Label" value={editDialog.item?.displayLabel || ''} onChange={(e) => updateEditItem('displayLabel', e.target.value)} fullWidth />
                <TextField label="Description" value={editDialog.item?.description || ''} onChange={(e) => updateEditItem('description', e.target.value)} fullWidth />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Field Type</InputLabel>
                    <Select value={editDialog.item?.fieldType || 'TEXT'} label="Field Type" onChange={(e) => updateEditItem('fieldType', e.target.value)}>
                      <MenuItem value="TEXT">TEXT</MenuItem>
                      <MenuItem value="NUMBER">NUMBER</MenuItem>
                      <MenuItem value="BOOLEAN">BOOLEAN</MenuItem>
                      <MenuItem value="DATE">DATE</MenuItem>
                      <MenuItem value="DATETIME">DATETIME</MenuItem>
                      <MenuItem value="DROPDOWN">DROPDOWN</MenuItem>
                      <MenuItem value="REFERENCE">REFERENCE</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Display Order" type="number" value={editDialog.item?.displayOrder || 0} onChange={(e) => updateEditItem('displayOrder', parseInt(e.target.value))} fullWidth />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel control={<Switch checked={editDialog.item?.isRequired ?? false} onChange={(e) => updateEditItem('isRequired', e.target.checked)} />} label="Required" />
                  <FormControlLabel control={<Switch checked={editDialog.item?.isEditable ?? true} onChange={(e) => updateEditItem('isEditable', e.target.checked)} />} label="Editable" />
                  <FormControlLabel control={<Switch checked={editDialog.item?.isVisibleInTable ?? true} onChange={(e) => updateEditItem('isVisibleInTable', e.target.checked)} />} label="Visible in Table" />
                </Box>
              </>
            )}

            {editDialog.type === 'action' && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Data Master</InputLabel>
                    <Select value={editDialog.item?.entityId || ''} label="Data Master" onChange={(e) => updateEditItem('entityId', e.target.value)}>
                      {entities.map(e => (
                        <MenuItem key={e.id} value={e.id}>{e.displayName || e.entityName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField label="Action Name" value={editDialog.item?.functionName || ''} onChange={(e) => updateEditItem('functionName', e.target.value)} fullWidth placeholder="e.g., GetAll, Create, Close" />
                </Box>
                <FormControl fullWidth>
                  <InputLabel>Linked API</InputLabel>
                  <Select value={editDialog.item?.apiId || ''} label="Linked API" onChange={(e) => updateEditItem('apiId', e.target.value)}>
                    {apis.map(a => (
                      <MenuItem key={a.id} value={a.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={a.httpMethod} size="small" sx={{ fontSize: 10 }} />
                          <span>{a.apiKey}</span>
                          <Typography variant="caption" color="text.secondary">{a.endpointPath}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="Description" value={editDialog.item?.description || ''} onChange={(e) => updateEditItem('description', e.target.value)} fullWidth multiline rows={2} />
                <TextField label="Parameter Mapping (JSON)" value={editDialog.item?.parameterMapping || ''} onChange={(e) => updateEditItem('parameterMapping', e.target.value)} fullWidth multiline rows={2} placeholder='{"id": "path.orderId"}' />
                <TextField label="Response Mapping" value={editDialog.item?.responseMapping || ''} onChange={(e) => updateEditItem('responseMapping', e.target.value)} fullWidth placeholder="e.g., data.items" />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Sort Order" type="number" value={editDialog.item?.sortOrder || 0} onChange={(e) => updateEditItem('sortOrder', parseInt(e.target.value))} sx={{ width: 120 }} />
                  <FormControlLabel control={<Switch checked={editDialog.item?.isActive ?? true} onChange={(e) => updateEditItem('isActive', e.target.checked)} />} label="Active" />
                </Box>
              </>
            )}

            {editDialog.type === 'tool' && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Tool Key" value={editDialog.item?.toolKey || ''} onChange={(e) => updateEditItem('toolKey', e.target.value)} fullWidth />
                  <TextField label="Tool Name" value={editDialog.item?.toolName || ''} onChange={(e) => updateEditItem('toolName', e.target.value)} fullWidth />
                </Box>
                <TextField label="Description" value={editDialog.item?.description || ''} onChange={(e) => updateEditItem('description', e.target.value)} fullWidth multiline rows={2} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Domain</InputLabel>
                    <Select value={editDialog.item?.domain || 'WMS'} label="Domain" onChange={(e) => updateEditItem('domain', e.target.value)}>
                      <MenuItem value="WMS">WMS</MenuItem>
                      <MenuItem value="FAST">FAST</MenuItem>
                      <MenuItem value="SYSTEM">SYSTEM</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select value={editDialog.item?.category || 'QUERY'} label="Category" onChange={(e) => updateEditItem('category', e.target.value)}>
                      <MenuItem value="QUERY">QUERY</MenuItem>
                      <MenuItem value="WIZARD">WIZARD</MenuItem>
                      <MenuItem value="GUIDED">GUIDED</MenuItem>
                      <MenuItem value="EXECUTION">EXECUTION</MenuItem>
                      <MenuItem value="CHECKPOINT">CHECKPOINT</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <TextField label="Prompt" value={editDialog.item?.prompt || ''} onChange={(e) => updateEditItem('prompt', e.target.value)} fullWidth multiline rows={4} />
                <FormControlLabel control={<Switch checked={editDialog.item?.active ?? true} onChange={(e) => updateEditItem('active', e.target.checked)} />} label="Active" />
              </>
            )}

            {editDialog.type === 'api' && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="API Key" value={editDialog.item?.apiKey || ''} onChange={(e) => updateEditItem('apiKey', e.target.value)} fullWidth />
                  <TextField label="API Name" value={editDialog.item?.apiName || ''} onChange={(e) => updateEditItem('apiName', e.target.value)} fullWidth />
                </Box>
                <TextField label="Description" value={editDialog.item?.description || ''} onChange={(e) => updateEditItem('description', e.target.value)} fullWidth />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>HTTP Method</InputLabel>
                    <Select value={editDialog.item?.httpMethod || 'GET'} label="HTTP Method" onChange={(e) => updateEditItem('httpMethod', e.target.value)}>
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                      <MenuItem value="DELETE">DELETE</MenuItem>
                      <MenuItem value="PATCH">PATCH</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Endpoint Path" value={editDialog.item?.endpointPath || ''} onChange={(e) => updateEditItem('endpointPath', e.target.value)} fullWidth placeholder="/api/v1/..." />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Domain" value={editDialog.item?.domain || ''} onChange={(e) => updateEditItem('domain', e.target.value)} fullWidth />
                  <TextField label="Entity Name" value={editDialog.item?.entityName || ''} onChange={(e) => updateEditItem('entityName', e.target.value)} fullWidth />
                </Box>
                <FormControlLabel control={<Switch checked={editDialog.item?.isActive ?? true} onChange={(e) => updateEditItem('isActive', e.target.checked)} />} label="Active" />

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Schema &amp; Samples (auto-generated -- re-run the schema backfill to refresh, not editable here)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <ReadOnlyJsonField label="Request Schema" value={editDialog.item?.requestSchema} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <ReadOnlyJsonField label="Sample Request" value={editDialog.item?.sampleRequest} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <ReadOnlyJsonField label="Response Schema" value={editDialog.item?.responseSchema} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <ReadOnlyJsonField label="Sample Response" value={editDialog.item?.sampleResponse} />
                  </Box>
                </Box>
              </>
            )}

            {editDialog.type === 'prompt' && (
              <>
                <TextField label="Prompt Name" value={editDialog.item?.name || ''} onChange={(e) => updateEditItem('name', e.target.value)} fullWidth required />
                <TextField label="Description" value={editDialog.item?.description || ''} onChange={(e) => updateEditItem('description', e.target.value)} fullWidth multiline rows={2} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select value={editDialog.item?.category || 'general'} label="Category" onChange={(e) => updateEditItem('category', e.target.value)}>
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="system">System</MenuItem>
                      <MenuItem value="agent">Agent</MenuItem>
                      <MenuItem value="wizard">Wizard</MenuItem>
                      <MenuItem value="analysis">Analysis</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Domain</InputLabel>
                    <Select value={editDialog.item?.domain || 'all'} label="Domain" onChange={(e) => updateEditItem('domain', e.target.value)}>
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="receiving">Receiving</MenuItem>
                      <MenuItem value="inventory">Inventory</MenuItem>
                      <MenuItem value="picking">Picking</MenuItem>
                      <MenuItem value="shipping">Shipping</MenuItem>
                      <MenuItem value="putaway">Putaway</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <TextField
                  label="Prompt Text"
                  value={editDialog.item?.promptText || ''}
                  onChange={(e) => updateEditItem('promptText', e.target.value)}
                  fullWidth
                  multiline
                  rows={8}
                  placeholder="Enter your system prompt here..."
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
                <TextField label="Tags (comma-separated)" value={editDialog.item?.tags || ''} onChange={(e) => updateEditItem('tags', e.target.value)} fullWidth placeholder="e.g., inventory, wms, receiving" />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={editDialog.item?.status || 'draft'} label="Status" onChange={(e) => updateEditItem('status', e.target.value)}>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="published">Published</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel control={<Switch checked={editDialog.item?.favorite ?? false} onChange={(e) => updateEditItem('favorite', e.target.checked)} />} label="Favorite" />
                </Box>
              </>
            )}

            {editDialog.type === 'connectedSystem' && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="System Code"
                    value={editDialog.item?.systemCode || ''}
                    onChange={(e) => updateEditItem('systemCode', e.target.value)}
                    fullWidth
                    required
                    placeholder="e.g., ACME_ERP"
                    disabled={!!editDialog.item?.id}
                    helperText={editDialog.item?.id ? "System code cannot be changed" : "Unique identifier for this system"}
                  />
                  <TextField
                    label="Name"
                    value={editDialog.item?.name || ''}
                    onChange={(e) => updateEditItem('name', e.target.value)}
                    fullWidth
                    required
                    placeholder="e.g., ACME ERP System"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>System Type</InputLabel>
                    <Select
                      value={editDialog.item?.systemType || 'ERP'}
                      label="System Type"
                      onChange={(e) => updateEditItem('systemType', e.target.value)}
                    >
                      <MenuItem value="ERP">ERP</MenuItem>
                      <MenuItem value="WMS">WMS</MenuItem>
                      <MenuItem value="TMS">TMS</MenuItem>
                      <MenuItem value="OMS">OMS</MenuItem>
                      <MenuItem value="WCS">WCS</MenuItem>
                      <MenuItem value="MES">MES</MenuItem>
                      <MenuItem value="OTHER">OTHER</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={<Switch checked={editDialog.item?.isActive ?? true} onChange={(e) => updateEditItem('isActive', e.target.checked)} />}
                    label="Active"
                    sx={{ ml: 2 }}
                  />
                </Box>
                <TextField
                  label="Base URL"
                  value={editDialog.item?.baseUrl || ''}
                  onChange={(e) => updateEditItem('baseUrl', e.target.value)}
                  fullWidth
                  placeholder="https://api.example.com"
                  helperText="The base URL for API calls to this system"
                />
                <TextField
                  label="API Key"
                  value={editDialog.item?.apiKey || ''}
                  onChange={(e) => updateEditItem('apiKey', e.target.value)}
                  fullWidth
                  type="password"
                  placeholder="Enter API key or leave blank"
                  helperText="Optional: API key for authentication"
                />
                {editDialog.item?.id && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    This system has {editDialog.item?.endpointCount || 0} configured endpoints.
                    Use the <SettingsIcon sx={{ fontSize: 14, verticalAlign: 'middle', mx: 0.5 }} /> button in the table to manage endpoints.
                  </Alert>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, type: null, item: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: null, item: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {deleteDialog.type}? This action will deactivate the item.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: null, item: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Endpoint Management Dialog */}
      <Dialog
        open={endpointDialog.open}
        onClose={() => { setEndpointDialog({ open: false, system: null, endpoints: [], loading: false }); setEditingEndpoint(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage Endpoints - {endpointDialog.system?.name || endpointDialog.system?.systemCode}
        </DialogTitle>
        <DialogContent>
          {endpointDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Endpoint List */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>URL</TableCell>
                      <TableCell>Public</TableCell>
                      <TableCell>Methods</TableCell>
                      <TableCell>Auth</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {endpointDialog.endpoints.map((endpoint) => (
                      <TableRow key={endpoint.id}>
                        <TableCell sx={{ fontWeight: 600 }}>{endpoint.endpointType}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{endpoint.url}</TableCell>
                        <TableCell>
                          <Chip
                            label={endpoint.isPublic ? 'Yes' : 'No'}
                            size="small"
                            color={endpoint.isPublic ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{endpoint.allowedMethods || 'ALL'}</TableCell>
                        <TableCell>{endpoint.authType || 'API_KEY'}</TableCell>
                        <TableCell>{endpoint.isActive ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEditEndpoint(endpoint)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteEndpoint(endpoint)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {endpointDialog.endpoints.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          No endpoints configured
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button startIcon={<AddIcon />} onClick={handleAddEndpoint} variant="outlined" sx={{ alignSelf: 'flex-start' }}>
                Add Endpoint
              </Button>

              {/* Endpoint Edit Form */}
              {editingEndpoint && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {editingEndpoint.id ? 'Edit Endpoint' : 'New Endpoint'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Endpoint Type</InputLabel>
                        <Select
                          value={editingEndpoint.endpointType || ''}
                          label="Endpoint Type"
                          onChange={(e) => setEditingEndpoint({ ...editingEndpoint, endpointType: e.target.value })}
                        >
                          <MenuItem value="HEALTH">HEALTH</MenuItem>
                          <MenuItem value="BASE_API">BASE_API</MenuItem>
                          <MenuItem value="AUTH">AUTH</MenuItem>
                          <MenuItem value="WEBHOOK">WEBHOOK</MenuItem>
                          <MenuItem value="INVENTORY_NOTIFICATION">INVENTORY_NOTIFICATION</MenuItem>
                          <MenuItem value="RECEIPT_NOTIFICATION">RECEIPT_NOTIFICATION</MenuItem>
                          <MenuItem value="ORDER_NOTIFICATION">ORDER_NOTIFICATION</MenuItem>
                          <MenuItem value="CUSTOM">CUSTOM</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="URL"
                        value={editingEndpoint.url || ''}
                        onChange={(e) => setEditingEndpoint({ ...editingEndpoint, url: e.target.value })}
                        fullWidth
                        size="small"
                        placeholder="/api/v1/..."
                      />
                    </Box>
                    <TextField
                      label="Description"
                      value={editingEndpoint.description || ''}
                      onChange={(e) => setEditingEndpoint({ ...editingEndpoint, description: e.target.value })}
                      fullWidth
                      size="small"
                    />
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editingEndpoint.isPublic || false}
                            onChange={(e) => setEditingEndpoint({ ...editingEndpoint, isPublic: e.target.checked })}
                          />
                        }
                        label="Public (no auth required)"
                      />
                      <TextField
                        label="Allowed Methods"
                        value={editingEndpoint.allowedMethods || ''}
                        onChange={(e) => setEditingEndpoint({ ...editingEndpoint, allowedMethods: e.target.value })}
                        size="small"
                        placeholder="GET,POST or empty for all"
                        sx={{ width: 200 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Auth Type</InputLabel>
                        <Select
                          value={editingEndpoint.authType || 'API_KEY'}
                          label="Auth Type"
                          onChange={(e) => setEditingEndpoint({ ...editingEndpoint, authType: e.target.value })}
                        >
                          <MenuItem value="NONE">None</MenuItem>
                          <MenuItem value="API_KEY">API Key</MenuItem>
                          <MenuItem value="OAUTH2">OAuth 2.0</MenuItem>
                          <MenuItem value="BASIC">Basic Auth</MenuItem>
                          <MenuItem value="HMAC">HMAC</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editingEndpoint.isActive ?? true}
                            onChange={(e) => setEditingEndpoint({ ...editingEndpoint, isActive: e.target.checked })}
                          />
                        }
                        label="Active"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button onClick={() => setEditingEndpoint(null)}>Cancel</Button>
                      <Button variant="contained" onClick={handleSaveEndpoint}>Save Endpoint</Button>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEndpointDialog({ open: false, system: null, endpoints: [], loading: false }); setEditingEndpoint(null); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FlexRegistryPanel;
